import { Spin, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Routes } from "../../../common/Routes";
import { isNullOrUndefined } from "../../../common/utils";
import BackTextComponent from "../../../components/BackTextComponent";
import { IsFeatureEnabledOrNot } from "../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../FeatureToggles/toggleList";
import useGetUserListApiCallStatus from "../userList/hooks/GetUserListApiCallStatus/GetUserListApiCallStatus";
import useCallApiToFetchUser from "./hooks/CallApiToFetchUser/CallApiToFetchUser";
import useGetUserDetail from "./hooks/GetUserDetail/GetUserDetail";
import useHandleUserNotFound from "./hooks/HandleUserNotFound/HandleUserNotFound";
import { UserGroupsListForUserViewActions } from "./model";
import { UserApplication } from "./tabs/UserApplication";
import UserGroupList from "./tabs/UserGroups/UserGroupList";
import UserInfo from "./tabs/UserInfo/UserInfo";
import { ReviewedAccesses } from "./tabs/userReviewedAccess";
import { sendUserEvent, UserAction } from "../../../components/matomoClient";
import { useGetUserGroupTypes } from "src/pages/userGroups/hooks/GetUserGroupTypeId";
import { AccessVisibility } from "../../../components/AccessVisibility";

export const UserPageBackText = "Back to Users";

const UserDetailsTabs = {
  UserInfo: {
    name: "User info",
    id: "user-info",
  },
  Applications: {
    name: "Applications",
    id: "application",
  },
  AccessVisibility: {
    name: "Access Visibility",
    id: "access-visibility",
  },
};

const { TabPane } = Tabs;

const UserTabKeyVsRoutes = {
  "access-visibility": "access-visibility",
};

function User({ match }) {
  const history = useHistory();
  const { id, tabName } = match.params;
  const [activeKey, setActiveKey] = useState(UserDetailsTabs.UserInfo.id);
  const [panes, setPanes] = useState([]);
  const dispatch = useDispatch();
  const userGroupTypes = useGetUserGroupTypes();
  const isUserGroupFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.UserGroups);
  useEffect(() => {
    if (isUserGroupFeatureEnabled)
      dispatch(UserGroupsListForUserViewActions.getUserGroupsListForUserView(id));
  }, [isUserGroupFeatureEnabled, id]);

  useEffect(() => {
    if (UserTabKeyVsRoutes[tabName] && activeKey !== UserTabKeyVsRoutes[tabName]) {
      setActiveKey(UserTabKeyVsRoutes[tabName]);
    }
  }, [tabName]);

  const handleNavigate = (tabKey) => {
    const currentUrl = window.location.pathname.replace(/\/$/, "");
    const route = UserTabKeyVsRoutes[tabKey];
    if (route && !currentUrl.includes(route)) {
      const updatedUrl = route ? `${currentUrl}/${route}` : currentUrl;
      history.push(updatedUrl);
    } else if (!route) {
      const value = Object.values(UserTabKeyVsRoutes).find((val) => currentUrl.includes(val));
      setActiveKey(tabKey);
      if (value) {
        history.push(currentUrl.substring(0, currentUrl.indexOf(value)));
      }
    }
  };


  function backButtonClickFunctionality() {
    history.push(Routes.Users.usersList.path);
    sendUserEvent(UserAction.BackToUserList);
  }

  function addTabPane(id, name, appId) {
    if (!panes.map((item) => item.id).includes(id)) {
      panes.push({ id, name: name, appId });
      setPanes(panes);
    }
    setActiveKey(id);
  }

  function editTabPane(targetKey, action) {
    if (action === "remove") {
      const newPanes = panes.filter((pane) => pane.id !== targetKey);
      setPanes(newPanes);
      setActiveKey(UserDetailsTabs.Applications.id);
    }
  }

  function renderUserGroupTabs() {
    return userGroupTypes.map((userGroupType) => {
      return (
        <TabPane tab={userGroupType.titleName} key={userGroupType.uniqueName} closable={false}>
          <UserGroupList userGroupType={userGroupType} />
        </TabPane>
      );
    });
  }

  function renderReviewedAccessesTabs() {
    return panes.map((pane) => {
      return (
        <TabPane tab={pane.name} key={pane.id} closable={true}>
          <ReviewedAccesses userId={userDetails.key} appId={pane.appId} />
        </TabPane>
      );
    });
  }

  const userDetails = useGetUserDetail(id);
  const { isApiCallMadeForUser } = useCallApiToFetchUser(id);
  useHandleUserNotFound(isApiCallMadeForUser, id);
  const { userListApiCallProgress, userListApiCallError } = useGetUserListApiCallStatus();

  return (
    <>
      <BackTextComponent
        backText={UserPageBackText}
        clickFunctionality={backButtonClickFunctionality}
      />
      {userListApiCallError ? (
        <div className="error-text">Error Fetching Details</div>
      ) : userListApiCallProgress || isNullOrUndefined(userDetails) ? (
        <Spin spinning={userListApiCallProgress} style={{ paddingTop: "20vh", width: "100%" }} />
      ) : (
        <>
          <div className="user-title">{userDetails.fullName}</div>
          <div className="user-title-sub-details">
            {userDetails.jobProfile}, {userDetails.homeOffice}
          </div>
          <Tabs
            className={"tabContainer"}
            type="editable-card"
            hideAdd
            activeKey={activeKey}
            onTabClick={(key) => {
              handleNavigate(key);
              sendUserEvent(UserAction.TabChange(key));
            }}
            onEdit={editTabPane}
          >
            <TabPane
              tab={UserDetailsTabs.UserInfo.name}
              key={UserDetailsTabs.UserInfo.id}
              closable={false}
            >
              <UserInfo userDetails={userDetails} />
            </TabPane>
            {renderUserGroupTabs()}
            <TabPane
              tab={UserDetailsTabs.AccessVisibility.name}
              key={UserDetailsTabs.AccessVisibility.id}
              closable={false}
            >
              <AccessVisibility
                userEmail={userDetails.email}
                toShowFor={{ userGroup: false, user: true }}
                userGroup={null}
              />
            </TabPane>
            <TabPane
              tab={UserDetailsTabs.Applications.name}
              key={UserDetailsTabs.Applications.id}
              closable={false}
            >
              <UserApplication userId={userDetails.key} addTabPane={addTabPane} />
            </TabPane>
            {renderReviewedAccessesTabs()}
          </Tabs>
        </>
      )}
    </>
  );
}

export default User;
